import React, { useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";

const OUTCOME_CONFIG = [
  { key: "applied",        label: "Applied",         color: "hsl(217 91% 55%)" },
  { key: "signed_up",      label: "Signed up",       color: "hsl(174 72% 56%)" },
  { key: "callback",       label: "Callback",        color: "hsl(38 92% 50%)" },
  { key: "no_answer",      label: "No answer",       color: "hsl(215 16% 57%)" },
  { key: "not_interested", label: "Not interested",  color: "hsl(0 72% 51%)" },
];

const PERIOD_LABELS = { today: "Today", week: "This week", month: "This month" };
const PERIODS = ["today", "week", "month"];
const PERIOD_BUTTON_LABELS = { today: "Day", week: "Week", month: "Month" };

const EMPTY_COUNTS = { applied: 0, signed_up: 0, callback: 0, no_answer: 0, not_interested: 0 };

const CallSummaryCard = ({ summary = {} }) => {
  const [period, setPeriod] = useState("today");

  const outcomes = useMemo(() => {
    const counts = summary[period] || EMPTY_COUNTS;
    return OUTCOME_CONFIG.map((cfg) => ({
      label: cfg.label,
      count: counts[cfg.key] || 0,
      color: cfg.color,
    }));
  }, [summary, period]);

  const totalCalls = outcomes.reduce((s, o) => s + o.count, 0);
  const maxCount = Math.max(...outcomes.map((o) => o.count), 1);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        backgroundColor: "#ffffff",
        p: 2.5,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
          {PERIOD_LABELS[period]}&rsquo;s summary
        </Typography>

        {/* Period toggle */}
        <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "hsl(140 8% 90%)", borderRadius: "8px", p: "2px", gap: "2px" }}>
          {PERIODS.map((p) => (
            <Box
              key={p}
              component="button"
              onClick={() => setPeriod(p)}
              sx={{
                border: "none",
                cursor: "pointer",
                fontSize: 11,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                px: "10px",
                py: "4px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
                ...(period === p
                  ? { backgroundColor: "#ffffff", color: "hsl(150 30% 12%)", boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" }
                  : { backgroundColor: "transparent", color: "hsl(150 10% 46%)", "&:hover": { color: "hsl(150 30% 12%)" } }),
              }}
            >
              {PERIOD_BUTTON_LABELS[p]}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Big number */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2.5 }}>
        <Typography sx={{ fontSize: 30, fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(150 30% 12%)", letterSpacing: "-0.025em", lineHeight: 1 }}>
          {totalCalls}
        </Typography>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
          calls made
        </Typography>
      </Box>

      {/* Outcome bars */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {outcomes.map((o) => (
          <Box key={o.label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", width: 96, flexShrink: 0, textAlign: "right" }}>
              {o.label}
            </Typography>
            <Box sx={{ flex: 1, height: 8, borderRadius: "9999px", backgroundColor: "hsl(140 8% 90%)", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  borderRadius: "9999px",
                  backgroundColor: o.color,
                  transition: "width 0.5s ease",
                  width: `${maxCount > 0 ? (o.count / maxCount) * 100 : 0}%`,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", width: 20, textAlign: "right" }}>
              {o.count}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Conversion rate */}
      <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid hsl(140 8% 88%)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
          Conversion rate
        </Typography>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 38% 12%)" }}>
          {totalCalls > 0 ? Math.round(((outcomes[0].count + outcomes[1].count) / totalCalls) * 100) : 0}%
        </Typography>
      </Box>
    </Paper>
  );
};

export default CallSummaryCard;
